import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu as MenuIcon, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { LOGO } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-[42px] left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#0F120E]/85 backdrop-blur-xl border-b border-[#2E3628]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 md:py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 leading-none"
        >
          <img
            src={LOGO.cream}
            alt="CHICA"
            className="h-9 md:h-11 w-auto object-contain"
            data-testid="header-logo-img"
          />
          <span className="hidden sm:flex flex-col leading-none border-l border-[#2E3628] pl-3">
            <span className="font-display text-xl tracking-[0.3em] text-[#F9F6F0]">
              CHICA
            </span>
            <span className="overline mt-1 text-[9px]">Fine Dining · Dehradun</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.25em] transition-colors ${
                  isActive ? "text-[#D4C1A0]" : "text-[#F9F6F0]/80 hover:text-[#D4C1A0]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <Link
            to="/reserve"
            data-testid="header-reserve-btn"
            className="hidden md:inline-flex btn-line py-2.5 px-5 text-[11px]"
          >
            Reserve
          </Link>
          <button
            data-testid="header-cart-btn"
            onClick={() => setOpen(true)}
            className="relative p-2 text-[#F9F6F0] hover:text-[#D4C1A0] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.2} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-1 bg-[#D4C1A0] text-[#0F120E] text-[10px] w-4 h-4 flex items-center justify-center font-medium"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobile((m) => !m)}
            className="lg:hidden p-2 text-[#F9F6F0]"
            aria-label="Open menu"
          >
            {mobile ? <X className="w-5 h-5" strokeWidth={1.2} /> : <MenuIcon className="w-5 h-5" strokeWidth={1.2} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden bg-[#0F120E]/95 backdrop-blur-xl border-t border-[#2E3628]/60"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="text-[14px] uppercase tracking-[0.25em] text-[#F9F6F0]/85"
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/reserve" data-testid="mobile-reserve-btn" className="btn-line w-fit">
              Reserve
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
